import { useState } from "react";
import { Check, Clipboard, Loader2, Smartphone, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  useCalendarSubscriptions,
  useCreateCalendarSubscription,
  useRevokeCalendarSubscription,
} from "@/hooks/use-calendar";

interface CalendarSubscriptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalendarSubscriptionsDialog({ open, onOpenChange }: CalendarSubscriptionsDialogProps) {
  const { toast } = useToast();
  const subscriptions = useCalendarSubscriptions(open);
  const create = useCreateCalendarSubscription();
  const revoke = useRevokeCalendarSubscription();
  const [oneTimeUrl, setOneTimeUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createSubscription = async (scope: "personal" | "company") => {
    try {
      const result = await create.mutateAsync(scope);
      setOneTimeUrl(result.url);
      setCopied(false);
    } catch (error: any) {
      toast({ title: "Could not create calendar link", description: error?.message, variant: "destructive" });
    }
  };

  const copy = async () => {
    if (!oneTimeUrl) return;
    try {
      await navigator.clipboard.writeText(oneTimeUrl);
      setCopied(true);
      toast({ title: "Calendar link copied" });
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the link manually.", variant: "destructive" });
    }
  };

  const revokeSubscription = async (id: string) => {
    try {
      await revoke.mutateAsync(id);
      toast({ title: "Calendar link revoked" });
    } catch (error: any) {
      toast({ title: "Could not revoke link", description: error?.message, variant: "destructive" });
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setOneTimeUrl(null);
      setCopied(false);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-lg max-h-[92vh] overflow-y-auto bg-gray-800 border-gray-600 text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Smartphone className="h-4 w-4" />Phone calendar</DialogTitle>
          <DialogDescription className="text-xs text-gray-400">
            These private subscription links are view-only. Create and edit entries in this PM interface.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-100">
          Treat a link like a password. It contains concise work titles, authorized project references and PM links, but never descriptions, attendee lists, or sensitive absence labels.
        </div>

        {oneTimeUrl && (
          <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-2 space-y-2">
            <div className="text-xs font-medium text-emerald-100">Copy this link now — it is shown only once.</div>
            <input className="h-8 w-full rounded border border-gray-600 bg-gray-950 px-2 text-[11px]" readOnly value={oneTimeUrl} onFocus={(event) => event.target.select()} aria-label="New calendar subscription URL" />
            <div className="flex gap-2">
              <Button type="button" size="sm" className="h-7 text-xs" onClick={copy}>{copied ? <Check className="h-3 w-3 mr-1" /> : <Clipboard className="h-3 w-3 mr-1" />}{copied ? "Copied" : "Copy"}</Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" className="h-8 text-xs" onClick={() => createSubscription("personal")} disabled={create.isPending}>New personal link</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-gray-600" onClick={() => createSubscription("company")} disabled={create.isPending}>New company link</Button>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Your active links</div>
          {subscriptions.isLoading ? (
            <div className="p-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : subscriptions.isError ? (
            <p role="alert" className="text-xs text-red-300 p-2">Calendar links could not be loaded.</p>
          ) : (subscriptions.data?.items.length ?? 0) === 0 ? (
            <p className="text-xs text-gray-500 p-2">No active subscription links.</p>
          ) : subscriptions.data?.items.map((subscription) => (
            <div key={subscription.id} className="flex items-center justify-between rounded border border-gray-700 px-2 py-1.5">
              <div>
                <div className="text-xs capitalize">{subscription.scope} calendar</div>
                <div className="text-[10px] text-gray-500">Created {new Date(subscription.createdAt).toLocaleDateString()}</div>
              </div>
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-red-300" onClick={() => revokeSubscription(subscription.id)} disabled={revoke.isPending}><Trash2 className="h-3 w-3 mr-1" />Revoke</Button>
            </div>
          ))}
        </div>

        <ol className="list-decimal pl-5 text-xs text-gray-400 space-y-1">
          <li>Copy a new private link.</li>
          <li>On iPhone choose Settings → Calendar → Accounts → Add Subscribed Calendar. On Android, add it in Google Calendar on the web.</li>
          <li>Paste the link. Updates appear automatically; editing remains disabled by design.</li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}